import { z } from 'zod';

export const zAppInitParams = z.object({
  drToken: z.string(),
  redirectUrl: z.string().url().nullable(),
  languageCode: z.string().optional(),
  allowOrderFiltering: z.boolean(),
  allowOrderRowFiltering: z.boolean(),
  loginType: z.string(),
  email: z.string().email().nullable(),
  customerNumber: z.string().nullable(),
  initOrderKeys: z.string().array().optional(),
  initOrderLines: z.string().optional().array().optional(),
  returnType: z.string().optional().nullable(),
  filterBy: z.string().optional().nullable(),
})
.strip();

export const zAppInit = z.object({
  params: zAppInitParams,
  updateDefaultParams: z.function().args(zAppInitParams).returns(z.void()),
});

export const zImage = z.object({
  salesOrderLineId: z.number(),
  dataURL: z.string(),
  file: z.instanceof(File),
  reference: z.string().optional(),
  reason: z.string().optional(),
});

export const zFormsAnswers = z.object({
  returnFormComponentId: z.number(),
  salesOrderLineId: z.number(),
  answer: z.object({
    type: z.string(),
    value: z.any(),
  }),
  reason: z.string().optional(),
});

export const zReturnData = z.object({
  amount: z.number(),
  reason: z.string(),
  readableReason: z.string(),
  isClaim: z.boolean(),
  returnDescription: z.string(),
  images: zImage.array().optional(),
  returnForms: zFormsAnswers.array(),
});

// The format needed for a specific orderLine
export const zOrderLine = z.object({
  salesOrderLineId: z.number(),
  lineNumber: z.string().nullable().optional(),
  quantity: z.number(),
  shippedQuantity: z.number().nullable().optional(),
  mayBeReturned: z.boolean(),
  mayBeClaimed: z.boolean(),
  labelTranslationKey: z.string(),
  // product
  productNumber: z.string().nullable(),

  description: z.string().nullable(),
  variant: z.string().nullable(),
  variantDescription: z.string().nullable(),
  imageUrl: z.string().nullable(),
  // custom
  uid: z.string(),
  orderNumber: z.string(),
  orderDate: z.string().nullable().optional(),
});

export const zOrder = z.object({
  customerNumber: z.string(),
  orderNumber: z.string(),
  orderDate: z.string(),
  numberOfShippedOrderLines: z.number().nullable().optional(),
  orderNumberAliases: z.string().array().nullable(),
  orderLines: zOrderLine.array(),
});

export const zUpdateAndAddToReturnArgs = zOrderLine.extend({
  returnData: zReturnData,
  initialReturnReason: z.string().nullable().optional(),
});

export const zOrderLineForm = zOrderLine.extend({
  layout: z.enum(['orderLine', 'returnLine']),
  setOpen: z.function().args(z.boolean()).returns(z.void()),
  returnData: zReturnData.optional(),
});

export const zCarrierItem = z.object({
  carrier: z.string(),
  logoUrl: z.string(),
  service: z.string(),
  code: z.string(),
});

export const zCarrierSelected = z.object({
  service: z.string(),
  code: z.string(),
  logoUrl: z.string(),
  carrier: z.string(),
  numberOfParcels: z.number().optional(),
});

export const zSummaryOrderLines = z.object({
  salesOrderLineId: z.number(),
  returnedQuantity: z.number(),
  comment: z.string(),
  reasonCodes: z.object({
    id: z.number(),
    rrmId: z.number(),
    code: z.string(),
  }),
  imageReferences: z.string().array().nullable(),
  exchange: z.object({
    productNumber: z.string(),
    variant: z.string(),
  }).nullable(),
});

export const zSummaryItem = z.object({
  orderNumber: z.string(),
  returnFulfilmentType: z.string(),
  returnOrderLines: zSummaryOrderLines.array(),
});

export const zSummaryPostObject = z.object({
  returnOrders: zSummaryItem.array(),
  chosenCarrierService: z.string(),
});

export const zPickupContactInfo = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumber: z.string(),
});

export const zContactInfo = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  addressLine3: z.string().nullable(),
  addressLine4: z.string().nullable(),
  city: z.string().nullable(),
  district: z.string().nullable(),
  region: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
});

export const zReturnOrderLines = z.object({
  salesOrderLineId: z.number(),
  returnedQuantity: z.number(),
  reasonCode: z.object({
    id: z.number(),
    rrmId: z.number(),
  }),
  comment: z.string(),
  imageReferences: z.string().array(),
  exchange: z.object({
    productNumber: z.string(),
    variant: z.string(),
  }),
  returnFormsAnswers: z.object({
    returnFormComponentId: z.number(),
    answer: z.object({
      type: z.string(),
      value: z.any(),
    }),
  }),
});

export const zReturnItem = z.object({
  salesOrderNumber: z.string(),
  returnOrderNumber: z.string(),
  returnSource: z.string(),
  returnOrderDate: z.string(),
  returnFulfilmentType: z.string(),
  returnOrderLines: zReturnOrderLines.array(),
});

export const zReturnPostObject = z.object({
  customerNumber: z.string(),
  iso6391: z.string(),
  returnOrders: zReturnItem.array(),
  chosenCarrierService: z.string(),
  numberOfParcels: z.number(),
  summaryResult: z.object({
    adjustedForShipment: z.boolean(),
    policyApplied: z.boolean(),
  }),
  pickupContact: zPickupContactInfo,
  createShippingContact: z.boolean(),
  shippingContact: zContactInfo.nullable().optional(),
});

export const zOption = z.object({
  value: z.string(),
  label: z.string(),
});
